/**
 * Live Activity Feed — WooCommerce-style realtime shopper stream.
 * "Niice ถึงหน้าชำระเงิน · 15 วิที่แล้ว" with LINE avatars for identified
 * customers and a city fallback for anonymous visitors. Data from
 * useLiveFeed (10s poll, tenant-scoped server-side).
 *
 * Exports the full card (Realtime page) and a compact latest-N list
 * (home dashboard block).
 */

import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { toast } from 'sonner'
import { ChatwootLogo } from '@/components/icons/ChatwootLogo'
import { useChatPanelStore } from '@/lib/store/chatPanelStore'
import { fetchCustomerChatSummary } from '@/features/customers/api/customerConversation'
import {
  ChevronRight,
  CreditCard,
  MapPin,
  Eye,
  FileText,
  Heart,
  PackageCheck,
  ShoppingBag,
  ShoppingCart,
  Ticket,
  Truck,
  User,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiveFeed, type LiveFeedEvent } from '@/hooks/useLiveVisitors'

// Soft colored chip per action — light bg + strong text, dark-mode aware.
const KIND_META: Record<
  LiveFeedEvent['kind'],
  { icon: LucideIcon; label: string; chip: string }
> = {
  order: {
    icon: PackageCheck,
    label: 'สั่งซื้อสำเร็จ',
    chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  },
  checkout: {
    icon: CreditCard,
    label: 'ถึงหน้าชำระเงิน',
    chip: 'bg-primary/10 text-primary dark:bg-primary/15',
  },
  payment: {
    icon: CreditCard,
    label: 'เลือกวิธีชำระเงิน',
    chip: 'bg-primary/10 text-primary dark:bg-primary/15',
  },
  shipping: {
    icon: Truck,
    label: 'เลือกวิธีจัดส่ง',
    chip: 'bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-400',
  },
  coupon: {
    icon: Ticket,
    label: 'ใช้คูปอง',
    chip: 'bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-400',
  },
  quote: {
    icon: FileText,
    label: 'ขอใบเสนอราคา',
    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-400',
  },
  add_to_cart: {
    icon: ShoppingCart,
    label: 'หยิบใส่ตะกร้า',
    chip: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  },
  view_product: {
    icon: Eye,
    label: 'กำลังดู',
    chip: 'bg-muted text-muted-foreground',
  },
  cart: {
    icon: ShoppingBag,
    label: 'เปิดดูตะกร้า',
    chip: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  },
  favorites: {
    icon: Heart,
    label: 'ดูรายการโปรด',
    chip: 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400',
  },
  account: {
    icon: User,
    label: 'ดูหน้าบัญชี',
    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-400',
  },
}

/** simpleicons logo URL for a known platform label, or null. */
export function sourceLogoUrl(label: string): string | null {
  const slugs: Record<string, string> = {
    Facebook: 'facebook',
    Instagram: 'instagram',
    LINE: 'line',
    Google: 'google',
    TikTok: 'tiktok',
    YouTube: 'youtube',
  }
  return slugs[label] ? `https://cdn.simpleicons.org/${slugs[label]}` : null
}

/** Overlapping stack of actual visitor avatars + anonymous count (max 5 shown). */
export function VisitorStack({
  avatars,
  anonCount,
  size = 'h-5 w-5',
}: {
  avatars: string[]
  anonCount: number
  size?: string
}) {
  const shown = avatars.slice(0, 5)
  const extra = Math.max(0, avatars.length - 5) + anonCount
  return (
    <span className="flex items-center -space-x-1.5">
      {shown.map((a, i) => (
        <img
          key={`${a.slice(-12)}-${i}`}
          src={a}
          alt=""
          referrerPolicy="no-referrer"
          loading="lazy"
          className={`${size} rounded-full object-cover border-2 border-card ring-1 ring-border/30`}
          onError={(ev) => {
            // LINE avatar URLs expire when users change their photo — hide
            // the broken circle instead of showing a torn image
            ;(ev.target as HTMLImageElement).style.display = 'none'
          }}
        />
      ))}
      {extra > 0 && (
        <span
          className={`${size} rounded-full bg-muted border-2 border-card ring-1 ring-border/30 flex items-center justify-center text-[8px] font-medium text-muted-foreground`}
          title={`ผู้เข้าชมอื่นอีก ${extra} คน`}
        >
          +{extra}
        </span>
      )}
    </span>
  )
}

export function timeAgo(ts: string): string {
  const diffSec = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 1000))
  if (diffSec < 60) return `${diffSec} วิที่แล้ว`
  const min = Math.round(diffSec / 60)
  if (min < 60) return `${min} นาทีที่แล้ว`
  return `${Math.round(min / 60)} ชม.ที่แล้ว`
}

const PAYMENT_TH: Record<string, string> = {
  'bank transfer': 'โอนธนาคาร',
  bank_transfer: 'โอนธนาคาร',
  promptpay: 'พร้อมเพย์',
  cod: 'เก็บเงินปลายทาง',
  bank_transfer_cod: 'เก็บเงินปลายทาง',
  card: 'บัตรเครดิต',
  credit_card: 'บัตรเครดิต',
  stripe: 'บัตรเครดิต',
}

function detailLabel(e: LiveFeedEvent): string {
  if (!e.detail) return ''
  if (e.kind === 'order' || e.kind === 'payment') {
    return PAYMENT_TH[e.detail.toLowerCase()] ?? e.detail
  }
  return e.detail
}

const THB = new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 })

const ACCOUNT_PAGES: [RegExp, string][] = [
  [/\/account\/orders\/./, 'เปิดดูคำสั่งซื้อ'],
  [/\/account\/orders/, 'คำสั่งซื้อของฉัน'],
  [/\/account\/address/, 'ที่อยู่จัดส่ง'],
  [/\/account\/verify-phone/, 'ยืนยันเบอร์โทร'],
  [/\/account\/complete-profile/, 'กรอกข้อมูลโปรไฟล์'],
  [/\/account\/profile/, 'ข้อมูลส่วนตัว'],
  [/\/account\/coupon/, 'คูปองของฉัน'],
  [/\/account\/rewards/, 'คะแนนสะสม'],
  [/\/account\/login/, 'เข้าสู่ระบบ'],
  [/\/account\/register/, 'สมัครสมาชิก'],
  [/\/account\/scrub-profile/, 'โปรไฟล์สครับ'],
]

function accountPageLabel(path: string): string {
  for (const [re, label] of ACCOUNT_PAGES) if (re.test(path)) return label
  return 'หน้าบัญชี'
}


const PAY_LABELS: Record<string, { label: string; cls: string }> = {
  paid: { label: 'จ่ายแล้ว', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' },
  partial: { label: 'จ่ายบางส่วน', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' },
  unpaid: { label: 'รอชำระ', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' },
}

function CustomerBadge({ e }: { e: LiveFeedEvent }) {
  if (!e.name || e.cust_orders < 0) return null
  if (e.cust_orders === 0) {
    return (
      <span className="shrink-0 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
        ลูกค้าใหม่
      </span>
    )
  }
  return (
    <span
      className="shrink-0 text-[10px] font-normal text-muted-foreground"
      title={`ลูกค้าเก่า — ยอดซื้อสะสมที่ผ่านมาทั้งหมด (ไม่ใช่ตะกร้าปัจจุบัน)`}
    >
      เคยซื้อ {e.cust_orders} ครั้ง · รวม ฿{THB.format(e.cust_spend)}
    </span>
  )
}

function personLabel(e: LiveFeedEvent): string {
  if (e.name) return e.name
  return e.visitor_tag ? `ผู้เข้าชม #${e.visitor_tag}` : 'ผู้เข้าชม'
}

/** "Pak Kret, Nonthaburi" — district-level where GeoIP has it. */
function locationLabel(e: LiveFeedEvent): string {
  if (!e.city && !e.province) return ''
  if (e.city && e.province && e.city !== e.province) return `${e.city}, ${e.province}`
  return e.city || e.province
}

const SOURCE_NAMES: [RegExp, string][] = [
  [/instagram/, 'Instagram'],
  [/facebook|fb\./, 'Facebook'],
  [/line|naver/, 'LINE'],
  [/google/, 'Google'],
  [/tiktok/, 'TikTok'],
  [/youtube/, 'YouTube'],
]

export function sourceName(source: string): string {
  for (const [re, label] of SOURCE_NAMES) if (re.test(source)) return label
  return source
}

/** Referrer source, or — when the referrer is internal/absent — the in-app
 * browser as proof of origin (Facebook in-app browser ⇒ came from Facebook). */
function displaySource(e: LiveFeedEvent): string {
  if (e.source) return sourceName(e.source)
  const b = e.browser.toLowerCase()
  if (b.includes('facebook')) return 'Facebook'
  if (b.includes('instagram')) return 'Instagram'
  if (b.includes('tiktok')) return 'TikTok'
  if (b.includes('line')) return 'LINE'
  return ''
}

const BROWSER_LOGOS: [RegExp, string][] = [
  [/samsung/i, 'samsunginternet'],
  [/chrome/i, 'googlechrome'],
  [/safari/i, 'safari'],
  [/firefox/i, 'firefox'],
  [/instagram/i, 'instagram'],
  [/facebook/i, 'facebook'],
  [/line/i, 'line'],
  [/opera/i, 'opera'],
  [/tiktok/i, 'tiktok'],
]

function browserLogoSlug(browser: string): string | null {
  for (const [re, slug] of BROWSER_LOGOS) if (re.test(browser)) return slug
  return null
}

function deviceLabel(e: LiveFeedEvent): string {
  const os = e.os.toLowerCase()
  if (e.device === 'Mobile') {
    if (os.includes('ios')) return 'iPhone'
    if (os.includes('android')) return 'Android'
    return 'มือถือ'
  }
  if (e.device === 'Tablet') return os.includes('ios') ? 'iPad' : 'แท็บเล็ต'
  if (e.device === 'Desktop') {
    if (os.includes('mac')) return 'Mac'
    if (os.includes('windows')) return 'Windows'
    return 'เดสก์ท็อป'
  }
  return ''
}

function shortIp(ip: string): string {
  if (!ip) return ''
  if (ip.includes(':')) {
    const groups = ip.split(':')
    return groups.slice(0, 2).join(':') + ':…'
  }
  return ip
}

function BrowserBadge({ e }: { e: LiveFeedEvent }) {
  const label = deviceLabel(e)
  if (!label && !e.browser) return null
  const slug = browserLogoSlug(e.browser)
  return (
    <span
      className="hidden sm:flex shrink-0 items-center gap-1 text-[10px] font-normal text-muted-foreground"
      title={`${e.browser} · ${e.os}${e.os_version ? ' ' + e.os_version : ''}`}
    >
      {slug && (
        <img
          src={`https://cdn.simpleicons.org/${slug}`}
          alt={e.browser}
          loading="lazy"
          className="h-3 w-3"
          onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none' }}
        />
      )}
      {label}
    </span>
  )
}

// iOS-notification feel: springs for position/scale (drop in + soft settle),
// quick tweens for opacity/blur so the card sharpens as it lands
export const ROW_MOTION = {
  layout: true as const,
  initial: { opacity: 0, y: -28, scale: 0.96, filter: 'blur(8px)' },
  animate: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' },
  exit: {
    opacity: 0,
    scale: 0.97,
    filter: 'blur(4px)',
    transition: { duration: 0.22, ease: 'easeIn' as const },
  },
  transition: {
    type: 'spring' as const,
    stiffness: 420,
    damping: 32,
    mass: 0.9,
    opacity: { duration: 0.28, ease: 'easeOut' as const },
    filter: { duration: 0.32, ease: 'easeOut' as const },
  },
}

/** Opens the shopper's actual Chatwoot conversation (floating panel) —
 * same flow as the customer page's "Open LINE conversation" button.
 * Falls back to /inbox when no conversation exists. */
function ChatShortcut({ e }: { e: LiveFeedEvent }) {
  const openConversation = useChatPanelStore((s) => s.openConversation)
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  if (!e.line_uid) return null
  const onClick = async (ev: React.MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    if (busy) return
    setBusy(true)
    try {
      const chat = await fetchCustomerChatSummary({ lineUserId: e.line_uid, limit: 1 })
      if (chat.account_id && chat.conversation_id) {
        openConversation(chat.account_id, chat.conversation_id)
      } else {
        // LINE login on the storefront ≠ has ever messaged the OA — say so
        // instead of dumping the owner into the inbox with no context
        toast.info(`${personLabel(e)} ยังไม่เคยทักแชทเข้ามา`, {
          description: 'ลูกค้าล็อกอินด้วย LINE แต่ยังไม่มีบทสนทนาใน Inbox',
        })
      }
    } catch {
      navigate('/inbox')
    } finally {
      setBusy(false)
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title="เปิดแชทของลูกค้าคนนี้"
      className={cn('transition-opacity', busy ? 'opacity-100 animate-pulse' : 'opacity-50 hover:opacity-100')}
    >
      <ChatwootLogo className="h-3.5 w-3.5" />
    </button>
  )
}

export function FeedRow({
  event,
  onSelectPerson,
}: {
  event: LiveFeedEvent
  onSelectPerson?: (personKey: string, label: string) => void
}) {
  const meta = KIND_META[event.kind]
  const Icon = meta.icon
  const selectable = Boolean(onSelectPerson && event.person_key)
  const handleSelect = () => {
    if (selectable) onSelectPerson!(event.person_key, personLabel(event))
  }
  return (
    <div className="flex items-start gap-3 py-3">
      {/* Avatar (LINE profile) or icon fallback */}
      <div
        className={cn('shrink-0', selectable && 'cursor-pointer')}
        onClick={handleSelect}
        title={selectable ? 'ดูการเดินทางของคนนี้' : undefined}
      >
        {event.avatar ? (
          <img
            src={event.avatar}
            alt=""
            referrerPolicy="no-referrer"
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
            <User className="h-4 w-4 text-muted-foreground/60" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {/* Line 1: who + when */}
        <div className="flex items-baseline justify-between gap-2">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0 text-sm font-normal text-foreground">
            {event.customer_id ? (
              <Link
                to={`/customers/${encodeURIComponent(event.customer_id)}`}
                className="truncate hover:underline"
                title={`ดูโปรไฟล์ลูกค้า ${event.customer_id}`}
              >
                {personLabel(event)}
              </Link>
            ) : (
              <span
                className={cn('truncate', selectable && 'cursor-pointer hover:underline')}
                title={`${event.browser} · ${event.os}`}
                onClick={handleSelect}
              >
                {personLabel(event)}
              </span>
            )}
            <CustomerBadge e={event} />
            {locationLabel(event) && (
              <span
                className="flex min-w-0 shrink items-center gap-1 text-[10px] font-normal text-muted-foreground"
                title={`${locationLabel(event)}${event.country ? ' · ' + event.country : ''}`}
              >
                {event.country_code ? (
                  <img
                    src={`https://flagcdn.com/w40/${event.country_code}.png`}
                    alt={event.country}
                    loading="lazy"
                    className="h-3.5 w-3.5 rounded-full object-cover ring-1 ring-border/50"
                    onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none' }}
                  />
                ) : (
                  <MapPin className="h-3 w-3 text-muted-foreground/60" />
                )}
                <span className="hidden sm:inline truncate">{locationLabel(event)}</span>
              </span>
            )}
            <BrowserBadge e={event} />
            {displaySource(event) && (
              <span
                className="hidden sm:flex shrink-0 items-center gap-1 text-[10px] font-normal text-muted-foreground"
                title={`จาก ${displaySource(event)}`}
              >
                · จาก
                {sourceLogoUrl(displaySource(event)) ? (
                  <img
                    src={sourceLogoUrl(displaySource(event))!}
                    alt={displaySource(event)}
                    loading="lazy"
                    className="h-3 w-3"
                    onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none' }}
                  />
                ) : (
                  <>{displaySource(event)}</>
                )}
              </span>
            )}
            {event.ip && (
              <span
                className="hidden sm:inline shrink-0 text-[10px] font-normal tabular-nums text-muted-foreground/60"
                title={event.ip}
              >
                · {shortIp(event.ip)}
              </span>
            )}
          </p>
          <span className="flex shrink-0 items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {timeAgo(event.ts)}
            </span>
            <ChatShortcut e={event} />
          </span>
        </div>

        {/* Line 2: action chip + thumbnail + product */}
        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
              meta.chip,
            )}
          >
            <Icon className="h-3 w-3" />
            {meta.label}
          </span>
          {event.product && (
            <span className="flex items-center gap-1.5 min-w-0">
              {event.product_image && (
                <img
                  src={event.product_image}
                  alt=""
                  loading="lazy"
                  className="h-5 w-5 rounded object-cover shrink-0 border border-border/40"
                />
              )}
              <span className="text-xs text-foreground truncate">{event.product}</span>
            </span>
          )}
          {event.kind === 'account' && (
            <span className="text-xs text-foreground truncate" title={event.path}>
              {accountPageLabel(event.path)}
            </span>
          )}
          {detailLabel(event) && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {detailLabel(event)}
            </span>
          )}
          {event.items.length > 0 && (
            <span className="flex items-center -space-x-1.5 shrink-0 pl-0.5">
              {event.items.slice(0, 4).map((it, i) =>
                it.image ? (
                  <img
                    key={`${it.name}-${i}`}
                    src={it.image}
                    alt={it.name}
                    title={`${it.name} ×${it.qty}`}
                    loading="lazy"
                    className="h-6 w-6 rounded-full object-cover border-2 border-card ring-1 ring-border/30"
                  />
                ) : (
                  <span
                    key={`${it.name}-${i}`}
                    title={`${it.name} ×${it.qty}`}
                    className="h-6 w-6 rounded-full bg-muted border-2 border-card ring-1 ring-border/30 flex items-center justify-center text-[9px] font-medium text-muted-foreground"
                  >
                    {it.name.slice(0, 1)}
                  </span>
                ),
              )}
              {event.items.length > 4 && (
                <span className="pl-2.5 text-[10px] text-muted-foreground">
                  +{event.items.length - 4}
                </span>
              )}
            </span>
          )}
          {event.amount > 0 && (
            <span className="shrink-0 text-xs font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
              · ฿{THB.format(event.amount)}
            </span>
          )}
          {event.pay_status && PAY_LABELS[event.pay_status] && (
            <span className={cn('inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium', PAY_LABELS[event.pay_status].cls)}>
              {PAY_LABELS[event.pay_status].label}
            </span>
          )}
          {event.view_count >= 2 && event.kind === 'view_product' && (
            <span className="shrink-0 text-[10px] text-muted-foreground">
              · ดู {event.view_count} ครั้งวันนี้
            </span>
          )}
          {event.open_cart_value > 0 && !['checkout', 'order', 'payment', 'shipping'].includes(event.kind) && (
            <span className="shrink-0 text-[10px] font-medium text-amber-600 dark:text-amber-400">
              · มีของในตะกร้า ฿{THB.format(event.open_cart_value)}
            </span>
          )}
          {event.pages_today >= 5 && (
            <span className="hidden sm:inline shrink-0 text-[10px] text-muted-foreground/60">
              · ดูแล้ว {event.pages_today} หน้าวันนี้
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/** Full feed card for the Realtime page. */
export function LiveActivityFeed({
  onSelectPerson,
}: {
  onSelectPerson?: (personKey: string, label: string) => void
} = {}) {
  const { data, isLoading } = useLiveFeed()

  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-foreground">เหตุการณ์ล่าสุด</h3>
        <Link
          to="/analytics/activity"
          className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          ดูทั้งหมด
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {isLoading ? (
        <div className="space-y-3 py-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
              <div className="h-3 flex-1 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
      ) : !data?.events.length ? (
        <p className="text-xs text-muted-foreground py-6 text-center">
          ยังไม่มีเหตุการณ์ใน 30 นาทีล่าสุด
        </p>
      ) : (
        <div className="divide-y divide-border/30 max-h-[420px] overflow-y-auto -mr-2 pr-2">
          <AnimatePresence initial={false}>
            {data.events.map((e) => (
              <motion.div key={`${e.ts}-${e.kind}-${e.path}`} {...ROW_MOTION}>
                <FeedRow event={e} onSelectPerson={onSelectPerson} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </section>
  )
}

/** Compact latest-N rows for the home dashboard realtime block. */
export function LiveActivityFeedCompact({ limit = 3 }: { limit?: number }) {
  const { data } = useLiveFeed()
  const events = (data?.events ?? []).slice(0, limit)
  if (!events.length) return null

  return (
    <div className="divide-y divide-border/30">
      <AnimatePresence initial={false}>
        {events.map((e) => (
          <motion.div key={`${e.ts}-${e.kind}-${e.path}`} {...ROW_MOTION}>
            <FeedRow event={e} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
